import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { APIContext, DialogAction } from "../../Contexts/APIContext";
import RoleCheck from "../../Authorisation/RoleCheck";
import { AuthContext } from "../../Authorisation/AuthContext";
import Fab from "@mui/material/Fab";
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Typography from "@mui/material/Typography";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DropDownViewModel from "../../Utilities/ViewModels/DropDownViewModel";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { SiteEditContext } from "../SiteEditContext";

interface IEditControlProps {
    readOnly: boolean;
    setReadOnly: Dispatch<SetStateAction<boolean>>;
    submit?: () => void;
    showSave?: boolean;
}

const fabStyle = { position: "absolute", bottom: 16, right: 16 };

export default function EditControl({ readOnly, setReadOnly, submit, showSave = true }: IEditControlProps) {
    const { loading, version, setVersion, get, openDialog } = useContext(APIContext);
    const { userDetails } = useContext(AuthContext);
    const { setIsEditing } = useContext(SiteEditContext);
    const [versions, setVersions] = useState<DropDownViewModel[]>([]);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string>("");
    const [defaultVersion, setDefault] = useState<number>(-1);
    const canEditAnyVersion = userDetails?.userEmail?.toLowerCase() === "owen.edwards@trics.org";

    useEffect(() => {
        setIsEditing(!readOnly);
    }, [readOnly, setIsEditing]);

    useEffect(() => {
        get("HomePage", "GetVersionDropdown", (data) => {
            setVersions(data.results);
            setDefault(version);
            const selectedVersion = data.results.find(
                (item) => item.id === version && (canEditAnyVersion || item.data === true)
            );
            const firstEditableVersion = selectedVersion ?? (canEditAnyVersion
                ? data.results[0]
                : data.results.find(f => f.data === true));

            setSelected(String(firstEditableVersion?.id ?? version));
        });
    }, [canEditAnyVersion, version]);

    const handleVersionChange = (event: SelectChangeEvent<string>) => {
        setSelected(event.target.value);
    };

    const closeDialog = () => {
        setOpen(false);
        setReadOnly(true);
        setVersion(defaultVersion);
    };

    const confirmVersion = () => {
        setOpen(false);
        const v = Number(selected);
        setVersion(v);
        setReadOnly(false);
    };

    const handleClick = () => {
        if (readOnly) {
            setOpen(true);
        } else {
            openDialog(
                <Typography>Are you sure you want to cancel? Any changes will not be saved.</Typography>,
                [new DialogAction("Cancel", () => { setReadOnly(true); })],
                "Warning",
                "Continue Editing"
            );
        }
    };

    return (
        <RoleCheck requriedRole={["Admin", "SuperAdmin"]}>
            <Fab color="primary" disabled={loading} onClick={handleClick} sx={!readOnly ? { ...fabStyle, right: 80 } : fabStyle}>
                {readOnly ? <EditIcon /> : <CloseIcon />}
            </Fab>
            {!readOnly && showSave &&
                (submit == undefined ?
                <Fab color="primary" disabled={loading} type="submit" sx={fabStyle}>
                    <SaveIcon />
                </Fab>
                :
                <Fab color="primary" disabled={loading} onClick={submit} sx={fabStyle}>
                    <SaveIcon />
                </Fab>)
            }

            <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>Please select an available version to edit</Typography>

                    <Select
                        value={selected}
                        onChange={handleVersionChange}
                        variant="outlined"
                        fullWidth
                        size="small"
                        displayEmpty
                        sx={{ ml: 1, minWidth: 240 }}
                    >
                        {versions.map((v) => (
                            <MenuItem
                                key={v.id}
                                value={String(v.id)}
                                disabled={!canEditAnyVersion && v.data !== true}
                            >
                                {v.label} {v.data === true ? " - Future Version" : ""}
                            </MenuItem>
                        ))}
                    </Select>

                    <DialogActions sx={{ px: 0 }}>
                        <Button onClick={confirmVersion} disabled={!selected}>Confirm Version</Button>
                        <Button onClick={closeDialog}>Cancel</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </RoleCheck>
    );
}
